import React, { useState, useEffect } from "react";
import {
  FaSave,
  FaEnvelope,
  FaSpinner,
  FaUserShield,
  FaUser,
  FaShieldAlt,
  FaBell,
  FaCog,
  FaKey,
  FaEye,
  FaTimes,
  FaEyeSlash,
  FaLock,
  FaWhatsapp,
  FaHashtag,
  FaDatabase,
  FaGlobe,
  FaFileInvoiceDollar,
  FaHistory,
  FaMobileAlt,
} from "react-icons/fa";
import api from "../../services/api";
import CustomDropdown from "./CustomDropdown";

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "", show: false });
  const [notificationSubTab, setNotificationSubTab] = useState("email");

  // Fetch System Status
  useEffect(() => {
    const fetchSystemStatus = async () => {
      try {
        const res = await api.get("/system/status");
        setSystemData((prev) => ({
          ...prev,
          maintenanceMode: res.data.maintenanceMode || false,
          maintenanceMessage: res.data.message || prev.maintenanceMessage,
          estimatedMinutes: res.data.estimatedMinutes || prev.estimatedMinutes,
        }));
      } catch (err) {
        console.error("Failed to fetch system status");
      }
    };
    fetchSystemStatus();
  }, []);

  // Fetch Admin Profile
  useEffect(() => {
    const fetchAdminProfile = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.id) return;
        const res = await api.get("/admin/me");
        setProfileData((prev) => ({
          ...prev,
          name: res.data.name || "",
          email: res.data.email || "",
          phone: res.data.mobile || "",
        }));
      } catch (err) {
        console.error("Failed to load admin profile", err);
      }
    };
    fetchAdminProfile();
  }, []);

  // NEW: Fetch Notification Credentials when Notifications tab is active
  useEffect(() => {
    const fetchAdminCredentials = async () => {
      try {
        const res = await api.get("/get/credential");
        if (res.data.success && res.data.data) {
          const cred = res.data.data;

          setNotificationData((prev) => ({
            ...prev,
            notificationEmail: cred.notificationEmail || "",
            whatsappVersion: cred.whatsappVersion || "",
            whatsappPhoneId: cred.whatsappPhoneId || "",
            // Sensitive fields are never returned by backend, so keep them empty
            emailPassword: "",
            whatsappToken: "",
            // Preserve existing toggles or use saved ones
            emailNotifications: cred.emailNotifications || prev.emailNotifications,
            pushNotifications: cred.pushNotifications || prev.pushNotifications,
            smsNotifications: cred.smsNotifications || prev.smsNotifications,
          }));
        }
      } catch (err) {
        console.error("Failed to fetch admin credentials", err);
      }
    };

    if (activeTab === "notifications") {
      fetchAdminCredentials();
    }
  }, [activeTab]);

  const timezoneOptions = [
    { value: "UTC", label: "UTC (Coordinated Universal Time)" },
    { value: "EST", label: "EST (Eastern Standard Time)" },
    { value: "PST", label: "PST (Pacific Standard Time)" },
    { value: "CET", label: "CET (Central European Time)" },
    { value: "IST", label: "IST (India Standard Time)" },
    { value: "GMT", label: "GMT (Greenwich Mean Time)" },
    { value: "AEST", label: "AEST (Australian Eastern Standard Time)" },
  ];

  const backupFrequencyOptions = [
    { value: "hourly", label: "Hourly" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
  ];

  const iconMap = {
    UTC: <FaGlobe className="text-gray-500" />,
    EST: <FaGlobe className="text-gray-500" />,
    PST: <FaGlobe className="text-gray-500" />,
    CET: <FaGlobe className="text-gray-500" />,
    IST: <FaGlobe className="text-gray-500" />,
    GMT: <FaGlobe className="text-gray-500" />,
    AEST: <FaGlobe className="text-gray-500" />,
  };

  // State Definitions
  const [profileData, setProfileData] = useState({
    name: "Admin User",
    email: "admin@example.com",
    password: "",
    phone: "+1 (555) 123-4567",
    title: "System Administrator",
    department: "IT",
  });

  const [securityData, setSecurityData] = useState({
    twoFactorEnabled: true,
    sessionTimeout: 30,
    loginNotifications: true,
    ipWhitelist: ["192.168.1.1", "10.0.0.1"],
    newIp: "",
  });

  const [notificationData, setNotificationData] = useState({
    notificationEmail: "admin@example.com",
    emailNotifications: {
      systemAlerts: true,
      securityAlerts: true,
      userActivity: false,
      weeklyReports: true,
    },
    pushNotifications: {
      criticalAlerts: true,
      newUsers: true,
      systemUpdates: false,
    },
    smsNotifications: {
      emergencyAlerts: true,
      twoFactorCodes: true,
    },
    emailPassword: "",
    whatsappVersion: "",
    whatsappPhoneId: "",
    whatsappToken: "",
  });

  const [systemData, setSystemData] = useState({
    maintenanceMode: false,
    maintenanceMessage:
      "We are performing scheduled maintenance. We'll be back soon!",
    estimatedMinutes: 30,
    backupFrequency: "daily",
    logRetention: 90,
    apiRateLimit: 1000,
    timezone: "UTC",
    language: "en",
    analyticsEnabled: true,
  });

  const showMessage = (text, type) => {
    setMessage({ text, type, show: true });
    setTimeout(() => {
      setMessage({ text: "", type: "", show: false });
    }, 3000);
  };

  const handleUpdateProfile = async () => {
    if (!profileData.name && !profileData.email && !profileData.password) {
      showMessage("Nothing to update", "error");
      return;
    }
    setLoading(true);
    try {
      await api.put("/admin/settings", profileData);
      showMessage("Profile updated successfully", "success");
      setProfileData({ ...profileData, password: "" });
    } catch (error) {
      showMessage("Failed to update profile", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSecurity = async () => {
    setLoading(true);
    try {
      await api.put("/admin/settings/security", securityData);
      showMessage("Security settings updated", "success");
    } catch (error) {
      showMessage("Failed to update security settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNotifications = async () => {
    setLoading(true);
    try {
      await api.put("/update/credential", notificationData);
      showMessage("Notification settings updated", "success");
    } catch (error) {
      showMessage("Failed to update notification settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSystem = async () => {
    setLoading(true);
    try {
      await api.put("/system/status", {
        maintenanceMode: systemData.maintenanceMode,
        message:
          systemData.maintenanceMessage.trim() ||
          "We are currently under maintenance.",
        estimatedMinutes: systemData.estimatedMinutes
          ? parseInt(systemData.estimatedMinutes)
          : null,
      });
      showMessage("System settings updated", "success");
    } catch (error) {
      showMessage("Failed to update system settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const addIpToWhitelist = () => {
    if (
      securityData.newIp &&
      !securityData.ipWhitelist.includes(securityData.newIp)
    ) {
      setSecurityData({
        ...securityData,
        ipWhitelist: [...securityData.ipWhitelist, securityData.newIp],
        newIp: "",
      });
    }
  };

  const removeIpFromWhitelist = (ip) => {
    setSecurityData({
      ...securityData,
      ipWhitelist: securityData.ipWhitelist.filter((item) => item !== ip),
    });
  };

  const tabs = [
    { id: "profile", label: "Admin Profile", icon: <FaUserShield /> },
    { id: "notifications", label: "Notifications", icon: <FaBell /> },
    { id: "system", label: "System", icon: <FaCog /> },
  ];

  const getSaveHandler = () => {
    switch (activeTab) {
      case "profile":
        return handleUpdateProfile;
      case "security":
        return handleUpdateSecurity;
      case "notifications":
        return handleUpdateNotifications;
      case "system":
        return handleUpdateSystem;
      default:
        return handleUpdateProfile;
    }
  };

  return (
    <div className="space-y-8">
      {/* Message Popup */}
      {message.show && (
        <div
          className={`fixed top-4 right-4 z-50 transform transition-all duration-300 ${message.type === "success" ? "bg-blue-500" : "bg-gray-400"
            } text-white px-6 py-3 rounded-xl shadow-2xl flex items-center space-x-3 animate-slide-in`}
        >
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${message.type === "success" ? "bg-blue-600" : "bg-gray-500"
              }`}
          >
            {message.type === "success" ? "✓" : "✗"}
          </div>
          <span className="font-medium">{message.text}</span>
          <button
            onClick={() => setMessage({ ...message, show: false })}
            className="ml-4 hover:opacity-80"
          >
            <FaTimes className="text-sm" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <FaCog className="mr-3 text-blue-500" />
              Admin Settings
            </h1>
            <p className="text-gray-600 mt-1">
              Manage your admin account and preferences
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="px-4 py-2.5 bg-blue-100 text-blue-600 rounded-xl font-medium">
              Last updated: Today
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Settings Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                Settings
              </h3>
            </div>
            <nav className="p-4 space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 ${activeTab === tab.id
                      ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                      : "text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  <span
                    className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${activeTab === tab.id
                        ? "bg-white/20"
                        : "bg-gray-100 text-gray-500"
                      }`}
                  >
                    {tab.icon}
                  </span>
                  <span className="text-left">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            {/* Profile Settings Tab */}
            {activeTab === "profile" && (
              <div>
                <div className="p-8 border-b border-gray-200">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mr-4">
                      <FaUserShield className="text-blue-500 text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">
                        Admin Profile Settings
                      </h3>
                      <p className="text-gray-600 mt-1">
                        Update your personal information
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-8 space-y-8">
                  <div className="flex items-center space-x-6">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-3xl font-bold">
                      A
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800">Profile Photo</h4>
                      <p className="text-gray-600 text-sm mt-1">
                        Recommended: 500x500px, max 2MB
                      </p>
                      <button className="mt-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors">
                        Change Photo
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          <div className="flex items-center">
                            <FaUser className="mr-2 text-gray-400" /> Full Name
                          </div>
                        </label>
                        <input
                          type="text"
                          placeholder="Enter your full name"
                          value={profileData.name}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              name: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          <div className="flex items-center">
                            <FaEnvelope className="mr-2 text-gray-400" /> Email Address
                          </div>
                        </label>
                        <input
                          type="email"
                          placeholder="admin@example.com"
                          value={profileData.email}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              email: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                        />
                        <p className="text-sm text-gray-500 mt-2">
                          This email will be used for all system notifications
                        </p>
                      </div>
                    </div>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          <div className="flex items-center">
                            <FaKey className="mr-2 text-gray-400" /> Password
                          </div>
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter new password"
                            value={profileData.password}
                            onChange={(e) =>
                              setProfileData({
                                ...profileData,
                                password: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 pr-12"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                          Leave blank to keep current password
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          <div className="flex items-center">
                            <FaMobileAlt className="mr-2 text-gray-400" /> Phone Number
                          </div>
                        </label>
                        <input
                          type="tel"
                          placeholder="+1 (555) 123-4567"
                          value={profileData.phone}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              phone: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                        />
                      </div>
                      <div className="bg-blue-50 rounded-xl p-5">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mr-3">
                            <FaShieldAlt className="text-blue-500" />
                          </div>
                          <h4 className="font-bold text-gray-800">
                            Security Tips
                          </h4>
                        </div>
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li className="flex items-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-2"></div>
                            Use a strong password with 8+ characters
                          </li>
                          <li className="flex items-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-2"></div>
                            Include numbers and special characters
                          </li>
                          <li className="flex items-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-2"></div>
                            Avoid using personal information
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Settings Tab */}
            {activeTab === "notifications" && (
              <div>
                <div className="p-8 border-b border-gray-200">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center mr-4">
                      <FaBell className="text-purple-500 text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">
                        Notification Settings
                      </h3>
                      <p className="text-gray-600 mt-1">
                        Configure how you receive notifications
                      </p>
                    </div>
                  </div>

                  <div className="flex space-x-1 border-b border-gray-200">
                    <button
                      onClick={() => setNotificationSubTab("email")}
                      className={`px-6 py-3 font-medium text-sm transition-colors ${notificationSubTab === "email"
                          ? "text-purple-600 border-b-2 border-purple-600"
                          : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                      Email
                    </button>
                    <button
                      onClick={() => setNotificationSubTab("whatsapp")}
                      className={`px-6 py-3 font-medium text-sm transition-colors ${notificationSubTab === "whatsapp"
                          ? "text-purple-600 border-b-2 border-purple-600"
                          : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                      WhatsApp
                    </button>
                  </div>
                </div>

                <div className="p-8 space-y-8">
                  {/* Email Tab Content */}
                  {notificationSubTab === "email" && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-5 bg-purple-50 rounded-xl border border-purple-100">
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            <div className="flex items-center">
                              <FaEnvelope className="mr-2 text-purple-500" /> Notification Email Address
                            </div>
                          </label>
                          <input
                            type="email"
                            placeholder="notifications@example.com"
                            value={notificationData.notificationEmail}
                            onChange={(e) =>
                              setNotificationData({
                                ...notificationData,
                                notificationEmail: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3.5 bg-white border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-700"
                          />
                          <p className="text-sm text-gray-600 mt-2">
                            All email notifications will be sent to this address (overrides profile email if set).
                          </p>
                        </div>

                        <div className="p-5 bg-purple-50 rounded-xl border border-purple-100">
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            <div className="flex items-center">
                              <FaLock className="mr-2 text-purple-500" /> Verification Password
                            </div>
                          </label>

                          <div className="relative">
                            <input
                              type={showEmailPassword ? "text" : "password"}
                              placeholder="Enter password to save changes"
                              value={notificationData.emailPassword || ""}
                              onChange={(e) =>
                                setNotificationData({
                                  ...notificationData,
                                  emailPassword: e.target.value,
                                })
                              }
                              className="w-full px-4 py-3.5 bg-white border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-700 pr-12"
                            />

                            <button
                              type="button"
                              onClick={() => setShowEmailPassword(!showEmailPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 hover:text-purple-700"
                            >
                              {showEmailPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>

                          <p className="text-sm text-gray-600 mt-2">
                            Required to update email notification settings.
                          </p>
                        </div>

                      </div>

                      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="space-y-4">
                          <div className="flex items-center mb-4">
                            <FaEnvelope className="text-purple-500 mr-2" />
                            <h4 className="font-bold text-gray-800">Email Notifications</h4>
                          </div>
                          {Object.entries(notificationData.emailNotifications).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                              <div>
                                <p className="font-medium text-gray-700 capitalize">
                                  {key.replace(/([A-Z])/g, " $1")}
                                </p>
                                <p className="text-sm text-gray-500">Receive email alerts</p>
                              </div>
                              <button
                                onClick={() =>
                                  setNotificationData({
                                    ...notificationData,
                                    emailNotifications: {
                                      ...notificationData.emailNotifications,
                                      [key]: !value,
                                    },
                                  })
                                }
                                className={`w-12 h-6 rounded-full transition-colors ${value ? "bg-green-500" : "bg-gray-300"}`}
                              >
                                <div
                                  className={`w-5 h-5 rounded-full bg-white transform transition-transform ${value ? "translate-x-7" : "translate-x-1"
                                    } mt-0.5`}
                                ></div>
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-center mb-4">
                            <FaBell className="text-blue-500 mr-2" />
                            <h4 className="font-bold text-gray-800">Push Notifications</h4>
                          </div>
                          {Object.entries(notificationData.pushNotifications).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                              <div>
                                <p className="font-medium text-gray-700 capitalize">
                                  {key.replace(/([A-Z])/g, " $1")}
                                </p>
                                <p className="text-sm text-gray-500">Browser notifications</p>
                              </div>
                              <button
                                onClick={() =>
                                  setNotificationData({
                                    ...notificationData,
                                    pushNotifications: {
                                      ...notificationData.pushNotifications,
                                      [key]: !value,
                                    },
                                  })
                                }
                                className={`w-12 h-6 rounded-full transition-colors ${value ? "bg-green-500" : "bg-gray-300"}`}
                              >
                                <div
                                  className={`w-5 h-5 rounded-full bg-white transform transition-transform ${value ? "translate-x-7" : "translate-x-1"
                                    } mt-0.5`}
                                ></div>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div> */}

                      <div className="p-5 bg-blue-50 rounded-xl">
                        <h4 className="font-bold text-gray-800 mb-2">Notification Preferences</h4>
                        <p className="text-gray-600">
                          Configure when and how you want to be notified about system events. Critical alerts are always sent regardless of these settings.
                        </p>
                      </div>
                    </>
                  )}

                  {/* WhatsApp Tab Content */}
                  {notificationSubTab === "whatsapp" && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="p-5 bg-green-50 rounded-xl border border-green-100">
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            <div className="flex items-center">
                              <FaWhatsapp className="mr-2 text-green-600 text-xl" /> WhatsApp Version
                            </div>
                          </label>
                          <input
                            type="text"
                            placeholder="e.g., v18.0"
                            value={notificationData.whatsappVersion || ""}
                            onChange={(e) =>
                              setNotificationData({
                                ...notificationData,
                                whatsappVersion: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3.5 bg-white border border-green-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-700"
                          />
                        </div>

                        <div className="p-5 bg-green-50 rounded-xl border border-green-100">
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            <div className="flex items-center">
                              <FaHashtag className="mr-2 text-green-600" /> WhatsApp Phone ID
                            </div>
                          </label>
                          <input
                            type="text"
                            placeholder="Phone Number ID"
                            value={notificationData.whatsappPhoneId || ""}
                            onChange={(e) =>
                              setNotificationData({
                                ...notificationData,
                                whatsappPhoneId: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3.5 bg-white border border-green-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-700"
                          />
                        </div>

                        <div className="p-5 bg-green-50 rounded-xl border border-green-100">
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            <div className="flex items-center">
                              <FaKey className="mr-2 text-green-600" /> WhatsApp Token
                            </div>
                          </label>
                          <input
                            type="password"
                            placeholder="Access Token"
                            value={notificationData.whatsappToken || ""}
                            onChange={(e) =>
                              setNotificationData({
                                ...notificationData,
                                whatsappToken: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3.5 bg-white border border-green-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-700"
                          />
                        </div>
                      </div>

                      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                        <div className="space-y-4">
                          <div className="flex items-center mb-4">
                            <FaMobileAlt className="text-green-500 mr-2" />
                            <h4 className="font-bold text-gray-800">SMS Notifications</h4>
                          </div>
                          {Object.entries(notificationData.smsNotifications).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                              <div>
                                <p className="font-medium text-gray-700 capitalize">
                                  {key.replace(/([A-Z])/g, " $1")}
                                </p>
                                <p className="text-sm text-gray-500">Text message alerts</p>
                              </div>
                              <button
                                onClick={() =>
                                  setNotificationData({
                                    ...notificationData,
                                    smsNotifications: {
                                      ...notificationData.smsNotifications,
                                      [key]: !value,
                                    },
                                  })
                                }
                                className={`w-12 h-6 rounded-full transition-colors ${value ? "bg-green-500" : "bg-gray-300"}`}
                              >
                                <div
                                  className={`w-5 h-5 rounded-full bg-white transform transition-transform ${value ? "translate-x-7" : "translate-x-1"
                                    } mt-0.5`}
                                ></div>
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-center mb-4">
                            <FaBell className="text-blue-500 mr-2" />
                            <h4 className="font-bold text-gray-800">Push Notifications</h4>
                          </div>
                          {Object.entries(notificationData.pushNotifications).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                              <div>
                                <p className="font-medium text-gray-700 capitalize">
                                  {key.replace(/([A-Z])/g, " $1")}
                                </p>
                                <p className="text-sm text-gray-500">Browser notifications</p>
                              </div>
                              <button
                                onClick={() =>
                                  setNotificationData({
                                    ...notificationData,
                                    pushNotifications: {
                                      ...notificationData.pushNotifications,
                                      [key]: !value,
                                    },
                                  })
                                }
                                className={`w-12 h-6 rounded-full transition-colors ${value ? "bg-green-500" : "bg-gray-300"}`}
                              >
                                <div
                                  className={`w-5 h-5 rounded-full bg-white transform transition-transform ${value ? "translate-x-7" : "translate-x-1"
                                    } mt-0.5`}
                                ></div>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div> */}

                      <div className="p-5 bg-green-50 rounded-xl">
                        <h4 className="font-bold text-gray-800 mb-2">WhatsApp Business API Settings</h4>
                        <p className="text-gray-600">
                          Configure your WhatsApp Business Cloud API credentials to enable WhatsApp notifications.
                          The toggles above control which events trigger notifications across all channels.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* System Settings Tab */}
            {activeTab === "system" && (
              <div>
                <div className="p-8 border-b border-gray-200">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center mr-4">
                      <FaCog className="text-gray-500 text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">
                        System Settings
                      </h3>
                      <p className="text-gray-600 mt-1">
                        Configure system-wide preferences
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-8 space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div className="flex items-center justify-between p-5 bg-gray-50 rounded-xl">
                        <div>
                          <h4 className="font-bold text-gray-800">
                            Maintenance Mode
                          </h4>
                          <p className="text-gray-600 text-sm mt-1">
                            Take the system offline for maintenance
                          </p>
                        </div>
                        <button
                          onClick={() =>
                            setSystemData({
                              ...systemData,
                              maintenanceMode: !systemData.maintenanceMode,
                            })
                          }
                          className={`w-12 h-6 rounded-full transition-colors ${systemData.maintenanceMode
                              ? "bg-red-500"
                              : "bg-gray-300"
                            }`}
                        >
                          <div
                            className={`w-5 h-5 rounded-full bg-white transform transition-transform ${systemData.maintenanceMode
                                ? "translate-x-7"
                                : "translate-x-1"
                              } mt-0.5`}
                          ></div>
                        </button>
                      </div>

                      <div className="p-5 bg-gray-50 rounded-xl">
                        <h4 className="font-bold text-gray-800 mb-3">
                          Estimated Downtime (minutes)
                        </h4>
                        <input
                          type="number"
                          min="5"
                          value={systemData.estimatedMinutes || ""}
                          onChange={(e) =>
                            setSystemData({
                              ...systemData,
                              estimatedMinutes: e.target.value
                                ? parseInt(e.target.value)
                                : null,
                            })
                          }
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="30"
                        />
                        <p className="text-sm text-gray-500 mt-2">
                          Used for countdown timer
                        </p>
                      </div>

                      <div className="p-5 bg-gray-50 rounded-xl">
                        <h4 className="font-bold text-gray-800 mb-4">
                          Backup Frequency
                        </h4>
                        <CustomDropdown
                          options={backupFrequencyOptions}
                          value={systemData.backupFrequency}
                          onChange={(val) =>
                            setSystemData({
                              ...systemData,
                              backupFrequency: val,
                            })
                          }
                          placeholder="Select backup frequency"
                          searchable={false}
                        />
                        <p className="text-sm text-gray-500 mt-3">
                          Automatic database backups
                        </p>
                      </div>

                      <div className="p-5 bg-gray-50 rounded-xl">
                        <h4 className="font-bold text-gray-800 mb-4">
                          Log Retention
                        </h4>
                        <div className="flex items-center space-x-4">
                          <input
                            type="range"
                            min="7"
                            max="365"
                            step="7"
                            value={systemData.logRetention}
                            onChange={(e) =>
                              setSystemData({
                                ...systemData,
                                logRetention: parseInt(e.target.value),
                              })
                            }
                            className="w-full"
                          />
                          <span className="font-bold text-blue-600 min-w-[80px]">
                            {systemData.logRetention} days
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-3">
                          Keep system logs for {systemData.logRetention} days
                        </p>
                      </div>
                    </div>
                    <div className="space-y-6">
                      <div className="p-5 bg-gray-50 rounded-xl">
                        <h4 className="font-bold text-gray-800 mb-4">
                          System Timezone
                        </h4>
                        <CustomDropdown
                          options={timezoneOptions}
                          value={systemData.timezone}
                          onChange={(val) =>
                            setSystemData({ ...systemData, timezone: val })
                          }
                          placeholder="Select timezone"
                          searchable={true}
                          iconMap={iconMap}
                        />
                        <div className="flex items-center mt-3 text-gray-500">
                          <FaGlobe className="mr-2" />
                          <p className="text-sm">
                            Affects all date/time displays
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="p-5 bg-red-50 rounded-xl border border-red-100">
                          <div className="flex items-center mb-3">
                            <FaDatabase className="text-red-500 mr-2" />
                            <h4 className="font-bold text-gray-800">
                              System Backup
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600 mb-4">
                            Create a manual backup of the entire system
                          </p>
                          <button className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
                            Backup Now
                          </button>
                        </div>
                        <div className="p-5 bg-yellow-50 rounded-xl border border-yellow-100">
                          <div className="flex items-center mb-3">
                            <FaHistory className="text-yellow-500 mr-2" />
                            <h4 className="font-bold text-gray-800">
                              Clear Cache
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600 mb-4">
                            Clear all cached data and temporary files
                          </p>
                          <button className="w-full px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors">
                            Clear Cache
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="p-8 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  Make sure to save your changes
                </div>
                <button
                  onClick={getSaveHandler()}
                  disabled={loading}
                  className="px-8 py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all duration-200 flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin mr-3" /> Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="mr-3" /> Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;